<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <header class="modal-header">
        <h3>
          <span class="material-icons modal-header__icon" aria-hidden="true">auto_awesome</span>
          {{ $t('plan_trail.ai_planner.title') }}
        </h3>
        <button class="close-btn" @click="close" :aria-label="$t('shared.close')">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </header>

      <div class="modal-body">
        <!-- How they walk -->
        <template v-if="step < QUESTION_COUNT">
          <p class="text-muted mb-1 step-label">{{ $t('plan_trail.ai_planner.step_of', { step: step + 1, total: STEP_COUNT }) }}</p>
          <p class="question-text">{{ PACE_QUESTIONS[step].question() }}</p>
          <div class="option-list">
            <button
              v-for="option in PACE_QUESTIONS[step].options"
              :key="option.key"
              type="button"
              class="option-btn"
              :class="{ active: answers[PACE_QUESTIONS[step].key] === option.key }"
              @click="answers[PACE_QUESTIONS[step].key] = option.key"
            >
              <span>{{ option.label() }}</span>
              <span class="option-btn__chevron" aria-hidden="true">›</span>
            </button>
          </div>
        </template>

        <!-- How many days that makes -->
        <template v-else-if="step === DAYS_STEP">
          <p class="text-muted mb-1 step-label">{{ $t('plan_trail.ai_planner.suggested_plan') }}</p>
          <div class="result-row">
            <button class="day-btn" type="button" :disabled="dayCount <= MIN_DAYS" :aria-label="$t('plan_trail.ai_planner.fewer_days')" @click="dayCount = clampDays(dayCount - 1)">
              <span class="material-icons" aria-hidden="true">remove</span>
            </button>
            <p class="result-days">{{ dayLabel }}</p>
            <button class="day-btn" type="button" :disabled="dayCount >= MAX_DAYS" :aria-label="$t('plan_trail.ai_planner.more_days')" @click="dayCount = clampDays(dayCount + 1)">
              <span class="material-icons" aria-hidden="true">add</span>
            </button>
          </div>
          <p class="result-detail">
            {{ $t('plan_trail.ai_planner.suggestion_detail', { distance: formattedDistance, ascent: formattedElevation, hours: formattedHours, hours_per_day: perDay }) }}
          </p>
        </template>

        <!-- Where they'd like to stay -->
        <template v-else-if="step === STAY_STEP">
          <p class="text-muted mb-1 step-label">{{ $t('plan_trail.ai_planner.step_of', { step: step, total: STEP_COUNT }) }}</p>
          <p class="question-text">{{ $t('plan_trail.ai_planner.stay_question') }}</p>
          <div class="option-list">
            <button
              v-for="option in STAY_CHOICES"
              :key="option.key"
              type="button"
              class="option-btn"
              :class="{ active: stay === option.key }"
              @click="stay = option.key"
            >
              <span>{{ option.label() }}</span>
              <span class="option-btn__chevron" aria-hidden="true">›</span>
            </button>
          </div>
          <p v-if="replacesDays" class="result-note">{{ $t('plan_trail.ai_planner.replaces_days') }}</p>
          <p v-if="joinsRoutes" class="result-note">{{ $t('plan_trail.ai_planner.joins_routes') }}</p>
        </template>

        <!-- The plan -->
        <template v-else>
          <p class="text-muted mb-1 step-label">{{ $t('plan_trail.ai_planner.your_plan', { days: dayLabel }) }}</p>
          <p v-if="loading" class="result-detail planner-busy">
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            {{ stay === 'none' ? $t('plan_trail.ai_planner.working_out') : $t('plan_trail.ai_planner.finding_places') }}
          </p>
          <p v-else-if="applying" class="result-detail planner-busy">
            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            {{ $t('plan_trail.ai_planner.applying') }}
          </p>
          <p v-else-if="!plan" class="result-detail">{{ $t('plan_trail.ai_planner.cannot_plan') }}</p>
          <template v-else>
            <p v-if="stay !== 'none' && plan.stops.length" class="result-detail mb-2">
              <template v-if="foundCount > 0">{{ $t('plan_trail.ai_planner.found_summary', { found: foundCount, total: plan.stops.length }) }}</template>
              <template v-else>{{ $t('plan_trail.ai_planner.found_none') }}</template>
            </p>
            <ol class="night-list">
              <li v-for="day in days" :key="day.day" class="night-item">
                <!-- Each night can be changed to somewhere they find themselves. -->
                <button
                  v-if="day.stop"
                  type="button"
                  class="night night--editable"
                  :class="{ 'night--editing': editingNight === day.day }"
                  :title="$t('plan_trail.ai_planner.change_stay')"
                  :aria-expanded="editingNight === day.day"
                  @click="toggleNight(day.day)"
                >
                  <span class="night__badge">{{ day.day }}</span>
                  <span class="night__text">
                    <span class="night__name">{{ dayName(day) }}</span>
                    <small class="night__meta">{{ dayMeta(day) }}</small>
                  </span>
                  <span class="material-icons night__search" aria-hidden="true">search</span>
                </button>
                <div v-else class="night">
                  <img class="night__finish" :src="finishIcon" alt="" />
                  <span class="night__text">
                    <span class="night__name">{{ dayName(day) }}</span>
                    <small class="night__meta">{{ dayMeta(day) }}</small>
                  </span>
                </div>
                <PlaceSearchPanel
                  v-if="day.stop && editingNight === day.day"
                  class="night__place-search"
                  :focus="nightFocus(day.stop)"
                  :load-places="() => loadNightPlaces(day.stop)"
                  @pick="pick => chooseStay(day.day - 1, pick)"
                  @close="editingNight = null"
                />
              </li>
            </ol>
          </template>
        </template>
      </div>

      <footer class="modal-footer">
        <button v-if="step > 0" class="btn btn-secondary" :disabled="applying" @click="back">{{ $t('plan_trail.ai_planner.back') }}</button>
        <button v-else class="btn btn-secondary" @click="close">{{ $t('plan_trail.ai_planner.cancel') }}</button>
        <button
          v-if="step < PLAN_STEP"
          class="btn btn-primary"
          :disabled="!canContinue"
          @click="next"
        >
          {{ $t('plan_trail.ai_planner.next') }}
        </button>
        <button v-else class="btn btn-primary" :disabled="loading || applying || !plan" @click="apply">
          {{ $t('plan_trail.ai_planner.apply', { count: dayCount }) }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, markRaw, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { i18n } from '../../../i18n';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { MIN_DAYS, MAX_DAYS } from '../utils/trailLine';
import {
  PACE_QUESTIONS,
  STAY_CHOICES,
  clampDays,
  hoursPerDay,
  nightsWithPlaces,
  paceFor,
  plannedDays,
  suggestedPlan,
} from '../utils/aiPlanner';
import {
  ENDPOINT_ACCOMMODATION_TYPES,
  ENDPOINT_AMENITY_TYPES,
  NEARBY_BUFFER_M,
  placeTypeLabel,
} from '../services/route-endpoints';
import { chooseNightStay } from '../services/night-planner';
import { pickCoordinates, pickName, pickTypeLabel } from '../services/place-picks';
import { placesAlongLine } from '../services/nearby-places';
import { pointAtDistance, sliceByDistance } from '../utils/trailLine';
import PlaceSearchPanel from './PlaceSearchPanel.vue';
import finishIcon from '../../../images/planner/route-finish.png';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  distanceMeters: {
    type: Number,
    default: 0,
  },
  elevationGainMeters: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'applied']);
const store = useStore();

// Three questions about how they walk, how many days that comes to, where
// they'd like to stay, then the plan itself (see utils/aiPlanner.ts).
const QUESTION_COUNT = PACE_QUESTIONS.length;
const DAYS_STEP = QUESTION_COUNT;
const STAY_STEP = QUESTION_COUNT + 1;
const PLAN_STEP = QUESTION_COUNT + 2;
/** Questions asked, as "Step n of …" counts them. */
const STEP_COUNT = QUESTION_COUNT + 1;

const step = ref(0);
const answers = ref({ flat: null, climb: null, hours: null });
const dayCount = ref(MIN_DAYS);
const stay = ref(null);
// What previewNightPlan found: { stops, totalM, line }, or null.
const plan = ref(null);
// The night whose stay is being searched for.
const editingNight = ref(null);
const loading = ref(false);
const applying = ref(false);

watch(() => props.isOpen, (open) => {
  if (open) {
    step.value = 0;
    answers.value = { flat: null, climb: null, hours: null };
    stay.value = null;
    plan.value = null;
    editingNight.value = null;
  }
});

const suggestion = computed(() => suggestedPlan(answers.value, props.distanceMeters, props.elevationGainMeters));
const perDay = computed(() => hoursPerDay(answers.value));
const dayLabel = computed(() => (dayCount.value === 1
  ? i18n.global.t('plan_trail.ai_planner.day_count_one')
  : i18n.global.t('plan_trail.ai_planner.day_count', { count: dayCount.value })));
const foundCount = computed(() => nightsWithPlaces(plan.value?.stops || []));
const days = computed(() => (plan.value ? plannedDays(plan.value.stops, plan.value.totalM) : []));
// What applying will do to the plan as it is (see applyNightPlan).
const replacesDays = computed(() => (store?.getters?.['planTrail/maxDay'] ?? 1) > 1
  || Object.keys(store?.state?.planTrail?.stageDestinations || {}).length > 0);
const joinsRoutes = computed(() => ['not-joined', 'several-routes'].includes(store?.getters?.['planTrail/daySpacingProblem']?.kind));

const canContinue = computed(() => {
  if (step.value < QUESTION_COUNT) return !!answers.value[PACE_QUESTIONS[step.value].key];
  if (step.value === STAY_STEP) return !!stay.value;
  return true;
});

const formattedDistance = computed(() => MeasurementPrefUtility.preference().smallUnitFormatter(props.distanceMeters, true));
const formattedElevation = computed(() => MeasurementPrefUtility.preference().smallUnitFormatter(props.elevationGainMeters, false));
const formattedHours = computed(() => `${suggestion.value.hours.toFixed(1)}h`);

function formatDistance(metres) {
  return MeasurementPrefUtility.preference().smallUnitFormatter(metres, true);
}

// Where the day ends: the finish, where they chose, the place found, or a point on the route.
function dayName(day) {
  if (!day.stop) return i18n.global.t('plan_trail.ai_planner.finish');
  if (day.stop.pick) return pickName(day.stop.pick);
  if (day.stop.place) return day.stop.place.name;
  return i18n.global.t('plan_trail.ai_planner.night_on_route', { distance: formatDistance(day.stop.atM) });
}

// "Day 1 · 17.8 km · B&B · 181m off the route"
function dayMeta(day) {
  const parts = [i18n.global.t('plan_trail.ai_planner.day_length', { day: day.day, distance: formatDistance(day.lengthM) })];
  const pick = day.stop?.pick;
  const place = day.stop?.place;
  if (pick || place) {
    const type = pick ? pickTypeLabel(pick) : placeTypeLabel(place.type);
    if (type) parts.push(type);
    parts.push(day.stop.offRouteM >= 50
      ? i18n.global.t('plan_trail.ai_planner.off_route', { distance: formatDistance(day.stop.offRouteM) })
      : i18n.global.t('plan_trail.ai_planner.on_route'));
  } else if (day.stop && stay.value !== 'none') {
    parts.push(i18n.global.t('plan_trail.ai_planner.nothing_near'));
  }
  return parts.join(' · ');
}

async function next() {
  if (!canContinue.value) return;
  // Arriving at the days, they start as the suggestion; going back and on
  // again after changing an answer re-suggests.
  if (step.value === QUESTION_COUNT - 1) dayCount.value = suggestion.value.days;
  step.value += 1;
  if (step.value === PLAN_STEP) await planNights();
}

function toggleNight(dayNumber) {
  editingNight.value = editingNight.value === dayNumber ? null : dayNumber;
}

// Where a night is now: where they chose, the place found, or its point on the route.
function nightFocus(stop) {
  if (stop.pick) return pickCoordinates(stop.pick);
  if (stop.place) return stop.place.coordinates;
  const line = plan.value?.line;
  if (!line) return null;
  const point = pointAtDistance(line.coordinates, line.cumDist, stop.atM);
  return [point[0], point[1]];
}

// Places along the stretch of route around a night — the same kinds a
// route's ends can be set to.
function loadNightPlaces(stop) {
  const line = plan.value?.line;
  if (!line) return Promise.resolve([]);
  const halfDayM = (plan.value.totalM / (plan.value.stops.length + 1)) / 2;
  const stretch = sliceByDistance(line.coordinates, line.cumDist, Math.max(0, stop.atM - halfDayM), stop.atM + halfDayM);
  return placesAlongLine(
    stretch,
    { accommodationTypes: ENDPOINT_ACCOMMODATION_TYPES, amenityTypes: ENDPOINT_AMENITY_TYPES },
    NEARBY_BUFFER_M
  );
}

function chooseStay(index, pick) {
  editingNight.value = null;
  if (!plan.value?.line) return;
  plan.value = { ...plan.value, stops: chooseNightStay(plan.value.line, plan.value.stops, index, pick) };
}

function back() {
  step.value = Math.max(0, step.value - 1);
}

async function planNights() {
  loading.value = true;
  plan.value = null;
  try {
    const found = await store.dispatch('planTrail/previewNightPlan', {
      dayCount: dayCount.value,
      pace: paceFor(answers.value),
      stayKind: stay.value === 'none' ? null : stay.value,
    });
    // The line is only read, and long — no need to make it reactive.
    plan.value = found ? { ...found, line: found.line ? markRaw(found.line) : null } : null;
  } catch (error) {
    console.error('AI Planner: could not plan the nights', error);
    plan.value = null;
  } finally {
    loading.value = false;
  }
}

async function apply() {
  if (!plan.value) return;
  applying.value = true;
  try {
    if (await store.dispatch('planTrail/applyNightPlan', { stops: plan.value.stops })) emit('applied');
  } finally {
    applying.value = false;
  }
  close();
}

function handleOverlayClick(event) {
  if (event.target === event.currentTarget && !applying.value) close();
}

function close() {
  emit('close');
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Bootstrap's modal level — above the map's own buttons (1000). */
  z-index: 1055;
}

.modal-content {
  background: var(--dark);
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
}

.modal-header h3 {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-header__icon {
  color: #fddf00;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #fff;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.step-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #a1a4ad;
}

.question-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0.35rem 0 1rem;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-align: left;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.option-btn__chevron {
  color: #6b6d76;
  font-size: 1.1rem;
  line-height: 1;
  flex-shrink: 0;
}

.option-btn:hover {
  border-color: rgba(253, 223, 0, 0.55);
}

.option-btn.active {
  border-color: #fddf00;
  background-color: rgba(253, 223, 0, 0.1);
  color: #fddf00;
}

.option-btn.active .option-btn__chevron {
  color: #fddf00;
}

.result-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0 0.6rem;
}

.result-days {
  font-size: 2.75rem;
  font-weight: 800;
  color: #fddf00;
  line-height: 1;
  margin: 0;
}

.day-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.day-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.result-detail {
  color: #a1a4ad;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
}

.result-note {
  margin: 0.75rem 0 0;
  color: #a1a4ad;
  font-size: 0.8rem;
}

.planner-busy {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.night-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.night {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.night--editable {
  width: 100%;
  padding: 0.25rem 0.4rem;
  border: none;
  border-radius: 8px;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.night--editable:hover,
.night--editing {
  background: rgba(255, 255, 255, 0.06);
}

.night__search {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 18px;
  color: #6b6d76;
}

.night--editable:hover .night__search,
.night--editing .night__search {
  color: #fddf00;
}

.night__place-search {
  margin: 0.4rem 0 0.25rem 2.3rem;
}

/* The same yellow stop badge as on the map (utils/stageBadgeIcon.ts). */
.night__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fddf00;
  border: 2px solid #17181d;
  color: #17181d;
  font-size: 0.7rem;
  font-weight: 700;
}

.night__finish {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
}

.night__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.night__name {
  color: #fff;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.night__meta {
  color: #a1a4ad;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border: none;
}
</style>
